import { Injectable, signal } from '@angular/core';

export interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  persistent?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private toasts = signal<ToastMessage[]>([]);

  // Expose the signal as readonly
  readonly toasts$ = this.toasts.asReadonly();

  private generateId(): string {
    return `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private addToast(toast: Omit<ToastMessage, 'id'>): void {
    const newToast: ToastMessage = {
      ...toast,
      id: this.generateId(),
      duration: toast.duration ?? 4000, // Reduced from 5000 to 4000ms for better UX
    };

    // Add to current toasts
    this.toasts.update((current) => [...current, newToast]);

    // Debug logs
    console.log('🍞 Toast added:', newToast);
    console.log('🍞 Current toasts count:', this.toasts().length);

    // Auto remove if not persistent
    if (!newToast.persistent && newToast.duration) {
      setTimeout(() => {
        this.removeToast(newToast.id);
      }, newToast.duration);
    }

    // Log to console for debugging
    console.log(
      `🍞 Toast [${toast.type.toUpperCase()}]:`,
      toast.title,
      toast.message
    );
  }

  showSuccess(message: string, title: string = 'Sucesso'): void {
    this.addToast({
      type: 'success',
      title,
      message,
    });
  }

  showError(message: string, title: string = 'Erro'): void {
    this.addToast({
      type: 'error',
      title,
      message,
      duration: 8000, // Errors stay longer
    });
  }

  showWarning(message: string, title: string = 'Aviso'): void {
    this.addToast({
      type: 'warning',
      title,
      message,
      duration: 6000,
    });
  }

  showInfo(message: string, title: string = 'Informação'): void {
    this.addToast({
      type: 'info',
      title,
      message,
    });
  }

  removeToast(id: string): void {
    this.toasts.update((current) => current.filter((toast) => toast.id !== id));
  }

  clearAll(): void {
    this.toasts.set([]);
  }
}
